import update from 'immutability-helper';
import { SET_NEW_SHOPPINGCART, SET_SHOPPINGCART } from './actionTypeConsts.js';

// ------------------------------------
// Helper
// ------------------------------------

function getAmountOfTicketsFromShoppingCart(shoppingCart) {
	const resultMap = {};
	if (!shoppingCart.events) return {};
	for (const event of shoppingCart.events) {
		for (const displayTicket of event.displayTickets) {
			if (!resultMap[displayTicket.priceLevelUuid]) {
				resultMap[displayTicket.priceLevelUuid] = displayTicket.numberOfSeats;
			} else {
				resultMap[displayTicket.priceLevelUuid] += displayTicket.numberOfSeats;
			}
		}
	}
	return resultMap;
}

function categoriesToAvailabilityMap(categories) {
	const map = {};
	for (const category of categories) {
		map[category.categoryUUID] = category.availableTickets;
	}
	return map;
}

function calcVolatileAvailability(currAvailability, currSelected, nextSelected) {
	return currAvailability + (currSelected - nextSelected);
}

// ------------------------------------
// Constants
// ------------------------------------

export const UPDATE_AMOUNT_OF_TICKETS = 'UPDATE_AMOUNT_OF_TICKETS';
export const SYNC_VOLATILE_AVAILABILITY = 'SYNC_VOLATILE_AVAILABILITY';
export const CHANGE_SELECTED_SHIPMENT_TYPE = 'CHANGE_SELECTED_SHIPMENT_TYPE';
export const CHANGE_SELECTED_PAYMENT_TYPE = 'CHANGE_SELECTED_PAYMENT_TYPE';
export const ADD_EXTRA = 'ADD_EXTRA';
export const REMOVE_EXTRA = 'REMOVE_EXTRA';
export const ADD_ERRORS = 'ADD_ERRORS';
export const CLEAR_ERRORS = 'CLEAR_ERRORS';
export const TOGGLE_PROCESSING_TICKETS = 'TOGGLE_PROCESSING_TICKETS';
export const SET_PROCESSING_TICKETS = 'SET_PROCESSING_TICKETS';
export const TOGGLE_SHOPPINGCART_ERROR = 'TOGGLE_SHOPPINGCART_ERROR';
export const UNSET_SHOPPINGCART_ERROR = 'UNSET_SHOPPINGCART_ERROR';

// ----------------------
// Non Actions Constans
// -----------------------
export const DECORATIVE_TICEKT = 'DECORATIVE_TICEKT';
export const GIFT_WRAPPER = 'GIFT_WRAPPER';
export const TICKET_INSURANCE = 'TICKET_INSURANCE';


export function updateAmountOfTickets(categoryUuid, priceLevelUuid, amount) {
	return {
		type: UPDATE_AMOUNT_OF_TICKETS,
		categoryUuid,
		priceLevelUuid,
		amount,
	};
}

export function syncVolatileAvailability(categories) {
	return {
		type: SYNC_VOLATILE_AVAILABILITY,
		categories,
	};
}

export function addTicketExtra(extra) {
	return {
		type: ADD_EXTRA,
		extra,
	};
}

export function removeTicketExtra(extra) {
	return {
		type: REMOVE_EXTRA,
		extra,
	};
}

export function addErrors(errors) {
	return {
		type: ADD_ERRORS,
		errors,
	};
}
export function clearErrors() {
	return {
		type: CLEAR_ERRORS,
	};
}
export function toggleProcessingTickets(processingTickets) {
	return {
		type: TOGGLE_PROCESSING_TICKETS,
		processingTickets,
	};
}
export function setProcessingTickets() {
	return {
		type: SET_PROCESSING_TICKETS,
	};
}
export function toggleShoppingCartError() {
	return {
		type: TOGGLE_SHOPPINGCART_ERROR,
	};
}

export function unsetShoppingCartError() {
	return {
		type: UNSET_SHOPPINGCART_ERROR,
	};
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
	[UPDATE_AMOUNT_OF_TICKETS]: (state, action) => ({
		...state,
		amountOfTickets: {
			...state.amountOfTickets,
			[action.priceLevelUuid]: action.amount,
		},
		volatileTicketAvailability: {
			...state.volatileTicketAvailability,
			[action.categoryUuid]: calcVolatileAvailability(
				state.volatileTicketAvailability[action.categoryUuid],
				state.amountOfTickets[action.priceLevelUuid] || 0,
				action.amount
			),
		},
	}),
	[SYNC_VOLATILE_AVAILABILITY]: (state, action) => update(state, {
		volatileTicketAvailability: { $set: categoriesToAvailabilityMap(action.categories) },
	}),
	[CHANGE_SELECTED_SHIPMENT_TYPE]: (state, action) => update(state, {
		selectedShipmentType: { $set: action.shipmentType },
	}),
	[CHANGE_SELECTED_PAYMENT_TYPE]: (state, action) => update(state, {
		selectedPaymentType: { $set: action.paymentType },
	}),
	[ADD_EXTRA]: (state, action) => update(state, {
		selectedExtras: { $push: [action.extra] },
	}),
	[REMOVE_EXTRA]: (state, action) => update(state, {
		selectedExtras: { $splice: [[state.selectedExtras.indexOf(action.extra)]] },
	}),
	[SET_SHOPPINGCART]: (state, action) => update(state, {
		amountOfTickets: { $set: getAmountOfTicketsFromShoppingCart(action.shoppingCart) },
		amountOfTicketsInCart: { $set: getAmountOfTicketsFromShoppingCart(action.shoppingCart) },
	}),
	[SET_NEW_SHOPPINGCART]: (state, action) => update(state, {
		amountOfTickets: { $set: getAmountOfTicketsFromShoppingCart(action.shoppingCart) },
		amountOfTicketsInCart: { $set: getAmountOfTicketsFromShoppingCart(action.shoppingCart) },
	}),
	[ADD_ERRORS]: (state, action) => {
		window.scrollTo(0, 0);
		return update(state, {
			errors: { $push: [...action.errors] },
		});
	},
	[CLEAR_ERRORS]: (state) => update(state, {
		errors: { $set: [] },
	}),
	[TOGGLE_PROCESSING_TICKETS]: (state) => update(state, {
		processingTickets: { $set: !state.processingTickets },
	}),
	[SET_PROCESSING_TICKETS]: (state, action) => update(state, {
		processingTickets: { $set: action.processingTickets },
	}),
	[TOGGLE_SHOPPINGCART_ERROR]: (state) => {
		window.scrollTo(0, 0);
		return update(state, {
			shoppingCartError: { $set: !state.shoppingCartError },
		});
	},
	[UNSET_SHOPPINGCART_ERROR]: (state) => update(state, {
		shoppingCartError: { $set: false },
	}),
};

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
	amountOfTickets: {}, // pricelevelUuid: amount
	amountOfTicketsInCart: {},
	/* volatileTicketAvailability object is approximately calculated on ticketSelecttion and then synched with backend values
	when the event is refreshed.
	This way we can provide instant rendering of the selected tickets in the dropdown while minimalizing the probability
	that the displayed availabilityValue and real availability in backend differs.*/
	volatileTicketAvailability: {}, // categoryUuid: quantity of available Tickets
	selectedExtras: [],
	errors: [],
	processingTickets: false,
	shoppingCartError: false,
};

export default function reducerFunction(state = initialState, action) {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
}
